import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { CartItem } from './CartItem';
import { formatPrice, cn } from '@/lib/utils';
import Button from '@/components/ui/Button';
import Spinner from '@/components/ui/Spinner';

export function CartDrawer() {
  const {
    items,
    itemCount,
    subtotal,
    isOpen,
    closeCart,
    updateQuantity,
    removeItem,
    isLoading
  } = useCart();

  const freeShippingThreshold = 150;
  const remainingForFreeShipping = freeShippingThreshold - subtotal;

  return (
    <Fragment>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 bg-black/50 z-40 transition-opacity duration-300',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={closeCart}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside
        className={cn(
          'fixed top-0 right-0 h-full w-full max-w-md bg-white z-50 shadow-2xl transition-transform duration-300 flex flex-col',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
        aria-label="Panier"
        aria-hidden={!isOpen}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-cream-200">
          <h2 className="font-display text-xl text-leather-900">
            Votre panier
            {itemCount > 0 && (
              <span className="ml-2 text-sm font-normal text-leather-500">
                ({itemCount} article{itemCount > 1 ? 's' : ''})
              </span>
            )}
          </h2>
          <button
            onClick={closeCart}
            className="p-2 text-leather-400 hover:text-leather-600 transition-colors rounded-lg hover:bg-cream-100"
            aria-label="Fermer le panier"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <Spinner size="lg" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full px-6 text-center">
              <svg
                className="w-20 h-20 text-cream-300 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                />
              </svg>
              <p className="text-leather-600 mb-6">Votre panier est vide</p>
              <Button onClick={closeCart} variant="secondary" as={Link} to="/shop">
                Découvrir nos produits
              </Button>
            </div>
          ) : (
            <div className="px-6 py-4">
              {/* Free Shipping Banner */}
              {remainingForFreeShipping > 0 && (
                <div className="mb-4 p-3 bg-gold-50 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-gold-800">
                    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                      />
                    </svg>
                    <span>
                      Plus que <strong>{formatPrice(remainingForFreeShipping)}</strong> pour la livraison gratuite
                    </span>
                  </div>
                  <div className="mt-2 h-1.5 bg-gold-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gold-500 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(100, (subtotal / freeShippingThreshold) * 100)}%` }}
                    />
                  </div>
                </div>
              )}

              {remainingForFreeShipping <= 0 && (
                <div className="mb-4 p-3 bg-success-50 rounded-lg flex items-center gap-2 text-sm text-success-700">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Livraison gratuite!</span>
                </div>
              )}

              {/* Cart Items */}
              <div className="divide-y divide-cream-200">
                {items.map((item) => (
                  <CartItem
                    key={`${item.product_id}-${item.variant_id || 'default'}`}
                    item={item}
                    onUpdateQuantity={(qty) => updateQuantity(item.product_id, item.variant_id, qty)}
                    onRemove={() => removeItem(item.product_id, item.variant_id)}
                    disabled={isLoading}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-cream-200 px-6 py-4 space-y-4 bg-white">
            {/* Subtotal */}
            <div className="flex justify-between items-center">
              <span className="text-leather-600">Sous-total</span>
              <span className="text-xl font-bold text-leather-900">{formatPrice(subtotal)}</span>
            </div>

            {/* Shipping Note */}
            <p className="text-xs text-leather-500 text-center">
              Frais de livraison calculés à l'étape suivante
            </p>

            {/* Checkout Button */}
            <Button
              as={Link}
              to="/checkout"
              onClick={closeCart}
              className="w-full"
              size="lg"
            >
              Passer commande
            </Button>

            {/* Continue Shopping */}
            <button
              onClick={closeCart}
              className="w-full text-center text-sm text-leather-600 hover:text-leather-900 transition-colors"
            >
              Continuer mes achats
            </button>
          </div>
        )}
      </aside>
    </Fragment>
  );
}

export default CartDrawer;
